import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { createHttpParamsFromRecord } from '../secure/common/helpers';
import { HasItems } from '../types';
import {
  ConfigurePanoBridgeRequest,
  ConnectEdgeDeviceToCameraRequest,
  EdgeDevice,
  EdgeDeviceActionResponse,
  EdgeDeviceConfig,
  EdgeDeviceNetworkConfig,
} from '../types/edge-device';

@Injectable({ providedIn: 'root' })
export class EdgeDeviceService {
  // both shoppersense and panobridge are edge_devices, which are not grouped under DevicesController
  apiHost = `${environment.apiBaseUrl}/Devices`;
  private httpClient = inject(HttpClient);

  constructor() {}

  // At least one branch_id is required
  getEdgeDevices(branchIds: number[]): Observable<HasItems<EdgeDevice>> {
    const params = createHttpParamsFromRecord({
      branch_ids: branchIds.filter(Boolean).map((id) => id.toString()),
    });
    return this.httpClient.get<HasItems<EdgeDevice>>(`${this.apiHost}`, { params });
  }

  // we accept both identifiers as a convenience for connecting.
  connectPanoBridgeEdgeDevice(deviceIdOrSerialNumber: string, request: ConfigurePanoBridgeRequest): Observable<EdgeDevice> {
    return this.httpClient.put<EdgeDevice>(`${this.apiHost}/${deviceIdOrSerialNumber}/PanoBridge`, request);
  }

  connectEdgeDeviceToCamera(deviceIdOrSerialNumber: string, request: ConnectEdgeDeviceToCameraRequest): Observable<EdgeDevice> {
    return this.httpClient.put<EdgeDevice>(`${this.apiHost}/${deviceIdOrSerialNumber}/Camera`, request);
  }

  // Disconnecting should just be pressing a button for users, hence why just id
  disconnectPanoBridgeEdgeDevice(deviceId: string): Observable<EdgeDevice> {
    return this.httpClient.delete<EdgeDevice>(`${this.apiHost}/${deviceId}/PanoBridge`);
  }
  disconnectEdgeDeviceFromCamera(deviceId: string): Observable<EdgeDevice> {
    return this.httpClient.delete<EdgeDevice>(`${this.apiHost}/${deviceId}/Camera`);
  }

  updateNetworkConfiguration(deviceId: string, networkConfiguration: EdgeDeviceNetworkConfig): Observable<EdgeDeviceConfig> {
    return this.httpClient.put<EdgeDeviceConfig>(`${this.apiHost}/${deviceId}/NetworkConfig`, networkConfiguration);
  }

  deleteNetworkConfiguration(deviceId: string): Observable<EdgeDeviceConfig> {
    return this.httpClient.delete<EdgeDeviceConfig>(`${this.apiHost}/${deviceId}/NetworkConfig`);
  }

  updateDevice(serialNumber: string): Observable<EdgeDeviceActionResponse> {
    return this.httpClient.post<EdgeDeviceActionResponse>(`${this.apiHost}/${encodeURIComponent(serialNumber)}/Update`, null);
  }

  rebootDevice(serialNumber: string): Observable<EdgeDeviceActionResponse> {
    return this.httpClient.post<EdgeDeviceActionResponse>(`${this.apiHost}/${encodeURIComponent(serialNumber)}/Reboot`, null);
  }

  isShopperSense(device: EdgeDevice): boolean {
    return (
      device.config?.device_mode === 'shopper_sense' ||
      device.config?.device_mode === 'shopper_sense_and_pano_bridge' ||
      !!device.config?.shopper_sense ||
      Boolean(device.camera)
    );
  }

  isPanoBridge(device: EdgeDevice): boolean {
    return (
      device.config?.device_mode === 'pano_bridge' ||
      device.config?.device_mode === 'shopper_sense_and_pano_bridge' ||
      !!device.config?.pano_bridge ||
      !device.camera
    );
  }
}
